import { DOMNode, Element } from 'html-react-parser';

/**
 * A RichText transform reshapes the parsed DOM before it is rendered. Transforms are used to
 * normalise authored (Sitecore Rich Text) HTML that is valid enough to store but produces
 * inaccessible or invalid markup when rendered as-is.
 *
 * To add a new accessibility normalisation:
 *   1. Write a transform below (take `nodes`, return the reshaped `nodes`).
 *   2. Add it to `a11yTransforms`.
 *   3. Cover it in `RichText.spec.tsx`.
 * It is then automatically applied by `RichText` for visitors and skipped in the Experience Editor.
 */
export type RichTextTransform = (nodes: DOMNode[]) => DOMNode[];

const isElementNamed = (node: DOMNode, name: string): node is Element =>
  node.type === 'tag' && (node as Element).name === name;

const isWhitespaceText = (node: DOMNode): boolean =>
  node.type === 'text' && (node as unknown as { data: string }).data.trim() === '';

/**
 * Collect a run of consecutive `<li>` items starting at `start`, treating whitespace-only text
 * nodes between items as part of the run. Returns the collected items and the index after the run.
 */
const collectListItemRun = (nodes: DOMNode[], start: number): { items: DOMNode[]; next: number } => {
  const items: DOMNode[] = [];
  let next = start;

  while (next < nodes.length) {
    if (isElementNamed(nodes[next], 'li')) {
      items.push(nodes[next]);
      next += 1;
      continue;
    }

    let lookahead = next;
    while (lookahead < nodes.length && isWhitespaceText(nodes[lookahead])) lookahead += 1;
    if (lookahead >= nodes.length || !isElementNamed(nodes[lookahead], 'li')) break;
    next = lookahead;
  }

  return { items, next };
};

/**
 * Wrap runs of orphaned `<li>` elements (those without a `<ul>`/`<ol>` parent) in a `<ul>`.
 * Orphaned list items are invalid HTML and an accessibility violation (WCAG 2.0 A 1.3.1).
 * List items that already have a `<ul>`/`<ol>` parent are left untouched.
 */
export const wrapOrphanedListItems = (nodes: DOMNode[], parentName?: string): DOMNode[] => {
  // Recurse first so orphans nested inside other elements (e.g. a <span>) are handled.
  nodes.forEach(node => {
    if (node.type !== 'tag') return;
    const element = node as Element;
    if (element.children?.length) {
      element.children = wrapOrphanedListItems(element.children as DOMNode[], element.name);
    }
  });

  // `<li>` elements are valid directly inside a list, so leave them as-is.
  if (parentName === 'ul' || parentName === 'ol') return nodes;

  const result: DOMNode[] = [];
  for (let index = 0; index < nodes.length; ) {
    if (!isElementNamed(nodes[index], 'li')) {
      result.push(nodes[index]);
      index += 1;
      continue;
    }

    const { items, next } = collectListItemRun(nodes, index);
    const list = new Element('ul', {}, items as Element['children']);
    items.forEach(item => (item.parent = list));
    result.push(list);
    index = next;
  }

  return result;
};

/**
 * Remove `<br>` elements that are direct children of a `<ul>`/`<ol>`. Lists may only directly
 * contain `<li>`, `<script>` or `<template>` (WCAG 2.0 A 1.3.1); stray `<br>` tags — often added
 * by the WYSIWYG editor to fake spacing — are invalid there and carry no content. A `<br>` inside
 * an `<li>` is a legitimate line break and is left untouched.
 */
export const stripListLineBreaks: RichTextTransform = nodes => {
  nodes.forEach(node => {
    if (node.type !== 'tag') return;
    const element = node as Element;
    if (!element.children?.length) return;

    // Recurse first so nested lists are handled.
    element.children = stripListLineBreaks(element.children as DOMNode[]) as Element['children'];

    if (element.name === 'ul' || element.name === 'ol') {
      element.children = element.children.filter(
        child => !isElementNamed(child as DOMNode, 'br'),
      ) as Element['children'];
    }
  });

  return nodes;
};

/**
 * An `<a>` is "empty" when it has no element children and no non-whitespace text, i.e. `<a></a>`
 * or `<a>   </a>`. An anchor that wraps an element (e.g. an `<img alt>`) is not considered empty,
 * since it can still expose an accessible name.
 *
 * Note: `aria-label`/`aria-labelledby`/`title` are intentionally NOT treated as a name here — the
 * RichText anchor render path (Anchor -> TextLink) does not forward those attributes to the DOM,
 * so such an anchor would still render as an unlabelled link. Removing it is the safer outcome.
 */
const isEmptyAnchor = (element: Element): boolean =>
  !element.children?.some(
    child => child.type === 'tag' || (child.type === 'text' && (child as unknown as { data: string }).data.trim()),
  );

/**
 * Remove `<a>` elements that have no discernible text (`<a></a>` / whitespace-only). Authored Rich
 * Text can contain such empty links, which render as unlabelled anchors — an accessibility
 * violation (WCAG 2.0 A 2.4.4 / 4.1.2). Links that contain elements (e.g. an image) or expose an
 * accessible name via attributes are kept.
 */
export const removeEmptyLinks: RichTextTransform = nodes => {
  // Recurse first so an anchor is evaluated against its cleaned children (e.g. nested empty <a>s).
  nodes.forEach(node => {
    if (node.type !== 'tag') return;
    const element = node as Element;
    if (element.children?.length) {
      element.children = removeEmptyLinks(element.children as DOMNode[]) as Element['children'];
    }
  });

  return nodes.filter(node => !(isElementNamed(node, 'a') && isEmptyAnchor(node)));
};

/**
 * Ordered registry of accessibility normalisations applied to authored HTML before render.
 * Append new transforms here to roll them out everywhere `RichText` is used.
 */
export const a11yTransforms: RichTextTransform[] = [wrapOrphanedListItems, stripListLineBreaks, removeEmptyLinks];
